<template>
  <div class="campaign-link-container">
    <div class="inline">
      <h4>{{ $t("app.campaign.single.inviteByLink") }}</h4>
    </div>
    <div class="link-text">
      {{ $t("app.campaign.single.linkDescription") }}
    </div>
    <div class="toggle-container">
      <div class="toggle" @click="enableCampaignLink">
        <input type="checkbox" @change="emit('link-updated', $event.target.checked)" v-model="props.linkActivated" />
        <label />
      </div>
      <span>{{ $t('app.campaign.settings.campaignLink.activateLinkButton') }}</span>
    </div>
    <transition name="fade">
      <div v-if="!isPending && props.linkActivated && props.publicInvitation">
        <InputCopy class="link-title" :label="$t('app.campaign.single.campaignLink')" :value="campaignShareLink" />
        <div>
          <h4 class="link-title">{{ $t("app.campaign.single.activationTitle") }}</h4>
          <div class="link-text">
            {{ $t("app.campaign.single.activationDescription") }}
          </div>
          <div class="radio-group">
            <label :class="['radio-option', 'radio-option-left', !state.requiresApproval ? 'radio-option-selected' : '']">
              <div class="radio-button">
                <input class="auto-radio-button-dot" type="radio" :checked="!state.requiresApproval" @change="handleRadioChange(false)" />
                <div class="radio-option-title">{{ $t("app.campaign.single.autoActivate") }}</div>
              </div>
              <div class="radio-option-text">
                {{ $t("app.campaign.single.autoActivateDesc") }}
              </div>
            </label>
            <label :class="['radio-option', state.requiresApproval ? 'radio-option-selected' : '']">
              <div class="radio-button">
                <input class="auto-radio-button-dot" type="radio" :checked="state.requiresApproval" @change="handleRadioChange(true)" />
                <div class="radio-option-title">{{ $t("app.campaign.single.manualActivate") }}</div>
              </div>
              <div class="radio-option-text">
                {{ $t("app.campaign.single.manualActivateDesc") }}
              </div>
            </label>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";
  .inline {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .link-title {
    margin-top: 16px!important;
  }

  .link-text {
    opacity: 0.8;
    font-style: italic;
  }

  .radio-group {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;

    .radio-button {
      input {
        margin: 0;
      }
  
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .radio-option-text {
      margin-top: 8px;
    }
  }

  .campaign-link-container {
    .toggle-container {
      display: flex;
      padding-top: 16px;
      gap: 16px;

      .toggle {
        position: relative;
        box-sizing: border-box;
      
        input[type="checkbox"] {
          position: absolute;
          left: 0;
          top: 0;
          z-index: 10;
          width: 100%;
          height: 100%;
          cursor: pointer;
          opacity: 0;
      
          &:checked,
          &.checked {
            &+ label:before {
              background: $primary-color;
            }
  
            &+ label:after {
              left: 20px;
            }
          }
      
          &:disabled {
            cursor: default;
                  
            &+ label:before {
              opacity: 0.5;
            }
          }

        }
      
        label {
          position: relative;
          display: flex;
          align-items: center;
          box-sizing: border-box;
      
          &::before {
            content: '';
            width: 42px;
            height: 24px;
            background: $secondary-50-color;
            position: relative;
            display: inline-block;
            border-radius: 13.5px;
            box-sizing: border-box;
            transition: 0.2s ease-in;
          }
      
          &::after {
            content: '';
            position: absolute;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            left: 2px;
            top: 2px;
            z-index: 1;
            background: var(--white-color);
            box-sizing: border-box;
            transition: 0.2s ease-in;
          }
        }
      }
    }
  }
</style>

<script setup lang="ts">
import { useCampaign } from "@/queries/campaigns.query";
import { Invitation } from "@/types/invitation";
const route = useRoute();

const { data: campaign, isPending } = useCampaign();

const emit = defineEmits<{
  (e: 'link-updated', value: boolean): void;
  (e: 'auto-activate', value: boolean)
}>();

const props = defineProps<{
  publicInvitation: Invitation,
  linkActivated: boolean,
}>();

const state = reactive({
  isLoading: false,
  requiresApproval: props?.publicInvitation?.requires_approval || false,
});

async function enableCampaignLink() {
  emit('link-updated', !props.linkActivated);
}

async function handleRadioChange(value) {
  state.requiresApproval = value;
  emit('auto-activate', value);
}

const campaignShareLink = computed(() => {
  if (!campaign.value || !props?.publicInvitation?.id)
    return null;
  return `${window.location.host.replace("app", "game")}/join?invitation_id=${
    props.publicInvitation.id
  }`;
});

watch(() => props.publicInvitation, () => {
  state.requiresApproval = props?.publicInvitation?.requires_approval || false;
});

</script>